import { AfterViewInit, ChangeDetectorRef, Component, OnDestroy, OnInit, TemplateRef, ViewChild } from '@angular/core';
import { Router } from '@angular/router';
import { FormControl } from '@angular/forms';
import { formatDate } from '@angular/common';
import { from, Subject, Subscription } from 'rxjs';
import { debounceTime, distinctUntilChanged, map, takeUntil } from 'rxjs/operators';
import { Store } from '@ngrx/store';
import { Actions, ofType } from '@ngrx/effects';

import { CustomTableComponent } from '../../../../../shared/ui/custom-table/components/table/custom-table.component';
import { TableConfig } from '../../../../../shared/ui/custom-table/model/table-config.model';
import { TableStateSnapshot } from '../../../../shared/utils/table-types';
import { buildSearchPayload, mapTableState, tableStateEquals } from '../../../../shared/utils/table-state.utils';
import { NotificationService } from '../../../../shared/services/notification.service';
import { ConfirmService } from '../../../../shared/services/confirm.service';
import { TierService } from '../../../../shared/services/tier.service';
import { ConfirmDialogDetail } from '../../../../shared/components/confirm-dialog.component';

import {
  LoadCustomerAdvancePayments,
  LoadCustomerAdvancePaymentsFailure,
  LoadCustomerAdvancePaymentsSuccess,
  CustomerAdvancePaymentActionTypes
} from '../../store/actions/customer-advance-payment.actions';
import { SummaryCustomerAdvancePaymentModel } from '../../models/summary-customer-advance-payment.model';
import { createCustomerAdvancePaymentTableConfig, TableRowAction } from './customer-advance-payment-table.config';

import { ActualSelected, Utils } from '../../../../../shared/utils';
import { consts } from '../../../../../consts';
import { AdvancePaymentService } from '../../../../../modules/expense/advancepayment/advancePayment.service';
import { DocumentAssignService } from '../../../shared/services/document-assign.service';

@Component({
  standalone: false,
  selector: 'app-customer-advance-payment-list',
  templateUrl: './customer-advance-payment-list.components.html',
  styleUrls: ['./customer-advance-payment-list.components.css']
})
export class CustomerAdvancePaymentListComponent implements OnInit, AfterViewInit, OnDestroy {

  @ViewChild('table') t1!: CustomTableComponent;
  @ViewChild('invoiceSummary') invoiceSummary!: TemplateRef<any>;
  @ViewChild('quickFilter') quickFilterTpl!: TemplateRef<any>;
  @ViewChild('expandDetail') expandDetailTpl!: TemplateRef<any>;

  tableConfig!: TableConfig;
  detailLoader = (uid: string) => from(this.advancePaymentService.loadData(uid)).pipe(
    map(advancePayment => ({ value: { invoiceLines: this.toDetailLines((advancePayment && advancePayment.advancedPaymentLines) || []) } }))
  );

  icon = 'payment';
  title = 'Anticipos de clientes';
  subTitle = 'Consulta y gestiona los anticipos recibidos de tus clientes';

  summary: any = null;

  monthsControl: FormControl = new FormControl([]);
  quickSearch = '';
  private readonly quickSearch$ = new Subject<string>();

  private lastState: TableStateSnapshot = {
    pageIndex: 0,
    pageSize: 10,
    filters: {},
    sortField: '',
    sortDirection: ''
  };

  private destroy$ = new Subject<void>();
  private subs: Subscription[] = [];

  constructor(
    private store: Store<any>,
    private actions$: Actions,
    private router: Router,
    private documentAssign: DocumentAssignService,
    private advancePaymentService: AdvancePaymentService,
    private notify: NotificationService,
    private confirm: ConfirmService,
    private tier: TierService,
    private cdr: ChangeDetectorRef,
  ) { }

  ngOnInit(): void {
    let defaultFilter: any = {};
    const stored = localStorage.getItem('viewSummaryCustomerAdvancePaymentFilters');
    if (stored) {
      try {
        defaultFilter = JSON.parse(stored);
        if (defaultFilter.pageSize == null) defaultFilter.pageSize = 10;
      } catch (e) { defaultFilter = {}; }
    }

    this.tableConfig = createCustomerAdvancePaymentTableConfig(
      {
        onCreate: () => this.create(),
        actionMap: {
          [TableRowAction.AssignIncome]: (row) => this.assignIncome(row),
          [TableRowAction.Edit]: (row) => this.edit(row),
          [TableRowAction.Delete]: (row) => this.delete(row),
        },
        selectable: false,
        summaryTemplate: this.invoiceSummary,
        columns: [
          { key: 'providerName', label: 'Cliente', sortable: true, sortKey: 'ProviderName', onCellClick: (row) => this.edit(row) },
          { key: 'date', label: 'Fecha', sortable: true, sortKey: 'Date', formatFn: (val) => val ? formatDate(val, 'dd/MM/yyyy', 'es-ES') : '' },
          { key: 'concept', label: 'Concepto', sortable: true, sortKey: 'Concept', ellipsis: true },
          { key: 'base', label: 'Base', sortable: true, sortKey: 'Base', formatFn: (val) => val != null ? Utils.FormatMoney(val) + ' €' : '' },
          { key: 'total', label: 'Total', sortable: true, sortKey: 'Total', formatFn: (val) => val != null ? Utils.FormatMoney(val) + ' €' : '' },
          { key: 'paymentMethodInfo', label: 'Método de cobro', sortable: false },
          {
            key: 'expenseNumber', label: 'Factura asignada', sortable: true, sortKey: 'ExpenseNumber',
            badge: {
              variantFn: (value: string) => value ? 'success' : 'warning',
              labelFn: (value: string) => value || 'Sin asignar',
            },
          },
        ],
        headerActions: [
          { label: '', icon: 'add', type: 'button', callback: () => this.create() },
        ],
        rowConfig: {},
      },
      {
        filterView: 'viewSummaryCustomerAdvancePaymentFilters',
        defaultFilter,
      }
    );

    this.tableConfig.rowConfig = {
      ...this.tableConfig.rowConfig,
      expandable: true,
      onExpand: () => {},
      onCollapse: () => {},
      onToggleExpand: () => {},
    };

    this.subs.push(
      this.actions$
        .pipe(
          ofType<LoadCustomerAdvancePaymentsSuccess>(CustomerAdvancePaymentActionTypes.LoadSuccess),
          map(action => action.payload)
        )
        .subscribe(res => {
          const expanded = new Set((this.t1.tableState.snapshot.data || []).filter((r: any) => r.__expanded).map((r: any) => r.uid));
          const items = (res.items || []).map(item => ({ ...item, __expanded: expanded.has(item.uid) }));
          this.summary = res.summary;
          this.t1.tableState.setData(items);
          this.t1.tableState.setTotalItems(res.total);
          this.t1.tableState.setLoading(false);
        })
    );

    this.subs.push(
      this.actions$
        .pipe(ofType<LoadCustomerAdvancePaymentsFailure>(CustomerAdvancePaymentActionTypes.LoadFailure))
        .subscribe(() => {
          this.t1.tableState.setLoading(false);
          this.notify.error('No se pudieron cargar los anticipos de clientes');
        })
    );
  }

  ngAfterViewInit(): void {
    this.tableConfig.summaryTemplate = this.invoiceSummary;
    this.tableConfig.quickFilterTemplate = this.quickFilterTpl;
    this.tableConfig.expandableRowTemplate = this.expandDetailTpl;

    this.monthsControl.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe((value: any) => {
        const raw = Array.isArray(value) ? value : [];
        const clean = raw.filter((v: any) => typeof v === 'number');
        if (clean.length !== raw.length) {
          this.monthsControl.setValue(clean, { emitEvent: false });
        }
        this.t1.tableState.patchFilters({ months: clean });
      });

    this.quickSearch$
      .pipe(debounceTime(300), distinctUntilChanged(), takeUntil(this.destroy$))
      .subscribe(value => this.t1.tableState.patchFilters({ search: value }));

    this.t1.tableState.state$
      .pipe(
        map(s => (s.filters && (s.filters as any).search) || ''),
        distinctUntilChanged(),
        takeUntil(this.destroy$),
      )
      .subscribe(v => { if (v !== this.quickSearch) this.quickSearch = v as string; });

    this.restoreStoredState();

    this.t1.tableState.state$
      .pipe(
        map(s => ((s.filters && (s.filters as any).months) || []) as number[]),
        distinctUntilChanged((a, b) => a.length === b.length && a.every((v, i) => v === b[i])),
        takeUntil(this.destroy$),
      )
      .subscribe(months => this.monthsControl.setValue(months, { emitEvent: false }));

    this.t1.tableState.state$
      .pipe(
        map(mapTableState),
        distinctUntilChanged(tableStateEquals),
        takeUntil(this.destroy$),
      )
      .subscribe(state => {
        this.lastState = state;
        localStorage.setItem('viewSummaryCustomerAdvancePaymentFilters', JSON.stringify(state));
        this.dispatchLoad();
      });

    this.cdr.detectChanges();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.subs.forEach(s => s.unsubscribe());
  }

  onSearchChange(value: string): void {
    this.quickSearch = value || '';
    this.quickSearch$.next(this.quickSearch);
  }

  create(): void {
    if (!this.tier.requireDocumentsTier()) return;
    this.router.navigate(['/invoice/advancepaymentinvoice/create']);
  }

  edit(row: SummaryCustomerAdvancePaymentModel): void {
    if (!this.tier.requireDocumentsTier()) return;
    if (this.isAssigned(row)) {
      this.notify.warning('No es posible editar un anticipo que ya está asignado a una factura');
      return;
    }
    this.router.navigate(['/invoice/advancepaymentinvoice/edit/', row.uid]);
  }

  delete(row: SummaryCustomerAdvancePaymentModel): void {
    if (!this.tier.requireDocumentsTier()) return;
    this.confirm.open({
      title: 'Eliminar anticipo',
      subtitle: row.providerName || undefined,
      message: this.isAssigned(row)
        ? `Este anticipo está asignado a la factura ${row.expenseNumber}: también se eliminará el cobro que generó en esa factura. Esta acción no se puede deshacer.`
        : '¿Seguro que quieres eliminar este anticipo? Esta acción no se puede deshacer.',
      tone: 'danger',
      details: this.advanceDetails(row),
      confirmText: 'Eliminar',
      confirmIcon: 'delete',
      loadingText: 'Eliminando anticipo…',
      errorText: 'No se pudo eliminar el anticipo',
      action: () => this.advancePaymentService.deleteData(row.uid),
    }).subscribe(deleted => {
      if (!deleted) return;
      this.notify.ok('Anticipo eliminado correctamente');
      this.dispatchLoad();
    });
  }

  assignIncome(row: SummaryCustomerAdvancePaymentModel): void {
    if (!this.tier.requireDocumentsTier()) return;
    if (this.isAssigned(row)) {
      this.notify.warning('Este anticipo ya está asignado a una factura');
      return;
    }
    this.documentAssign.assignAdvancePayment({
      uid: row.uid,
      totalAmount: row.total,
      clientUid: row.providerUid,
      clientName: row.providerName,
    }).subscribe(assigned => {
      if (assigned) this.dispatchLoad();
    });
  }

  private toDetailLines(lines: any[]): any[] {
    return lines.map(line => {
      const gross = (line.unityAmount || 0) * (line.quantity || 0);
      const base = Utils.RoundTwoDecimals(gross - gross * (line.discount || 0) / 100);
      return {
        ...line,
        totalLine: base,
        taxes: (line.taxes || []).map((tax: any) => ({
          ...tax,
          taxImport: Utils.RoundTwoDecimals(base * (tax.taxImport || 0) / 100),
        })),
      };
    });
  }

  private isAssigned(row: SummaryCustomerAdvancePaymentModel): boolean {
    return !!row.invoiceUid && row.invoiceUid !== consts.EMPTY_GUID;
  }

  private advanceDetails(row: SummaryCustomerAdvancePaymentModel): ConfirmDialogDetail[] {
    const details: ConfirmDialogDetail[] = [];
    if (row.date) details.push({ label: 'Fecha', value: formatDate(row.date, 'dd/MM/yyyy', 'es-ES') });
    if (row.concept) details.push({ label: 'Concepto', value: row.concept });
    details.push({ label: 'Total', value: Utils.FormatMoney(row.total) + ' €' });
    return details;
  }

  private restoreStoredState(): void {
    const stored = this.tableConfig.defaultFilter as Partial<TableStateSnapshot>;
    if (!stored) return;

    const tableState = this.t1.tableState;
    if (stored.pageSize) tableState.setPageSize(stored.pageSize);
    if (stored.filters && Object.keys(stored.filters).length) tableState.setFilters(stored.filters);
    if (stored.sortField) tableState.setSort(stored.sortField, stored.sortDirection || '');
  }

  private dispatchLoad(): void {
    const filter = { ...this.lastState, filters: { ...this.lastState.filters, year: ActualSelected.yearSelected } };
    this.t1.tableState.setLoading(true);
    this.store.dispatch(new LoadCustomerAdvancePayments({ filter: buildSearchPayload(filter) }));
  }
}
